import React, { useEffect, useState } from 'react';
import {
    Alert, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, List, ListItemButton, ListItemText, TextField, Typography,
} from "@mui/material";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import { LoadingButton } from '@mui/lab';
import centreServices from 'app/services/centre-services';

const DEFAULT_RECORD = { name: '', code: '', address: '' };
const INCLUDE = '?include=main_centre_zone.location';

// A qué nivel resolvió HERE. Una bodega tiene que quedar en su calle: resuelta a nivel de
// comuna o de región queda en el centro de la comuna, y desde ahí sale mal cada ruta.
const NIVEL_PRECISO = ['houseNumber', 'street', 'place'];
const NIVEL_LABEL = {
    houseNumber: 'calle y número',
    street: 'calle',
    place: 'lugar',
    locality: 'comuna',
    administrativeArea: 'región',
    postalCodePoint: 'código postal',
};

const esPreciso = (candidato) => NIVEL_PRECISO.includes(candidato?.result_type);

const CentreEditModal = (props) => {
    const { recordID, isOpen, onClose = () => {}, onRecordChange = () => {}, onRecordAdd = () => {} } = props;
    const [currentRecord, setCurrentRecord] = useState(null);
    const [loadingRecord, setLoadingRecord] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState([]);
    // La dirección guardada, la que se está escribiendo, los candidatos de HERE y el elegido.
    const [savedLocation, setSavedLocation] = useState(null);
    const [candidates, setCandidates] = useState(null);
    const [selected, setSelected] = useState(null);
    const [verifying, setVerifying] = useState(false);

    useEffect(() => {
        if (!isOpen) return;

        setErrors([]);
        setCandidates(null);
        setSelected(null);
        setSavedLocation(null);

        if (recordID) {
            loadRecord(recordID);
        } else {
            setCurrentRecord({ ...DEFAULT_RECORD });
        }
    }, [recordID, isOpen]);

    const loadRecord = (id) => {
        if (!id) return;
        setLoadingRecord(true);
        setCurrentRecord(null);
        centreServices.getCentre(id, INCLUDE)
            .then(resp => {
                const location = resp?.main_centre_zone?.location;
                setSavedLocation(location || null);
                // La dirección completa y no la recortada: es la que se compara con lo que se
                // escribe para saber si hay que volver a ubicar la bodega.
                setCurrentRecord({ ...resp, address: location?.address || '' });
            })
            .catch(() => setErrors(['No se pudo cargar la bodega']))
            .finally(() => setLoadingRecord(false));
    };

    const direccionEscrita = (currentRecord?.address || '').trim();
    const direccionCambio = direccionEscrita !== (savedLocation?.address || '').trim();
    const necesitaVerificar = direccionEscrita.length > 0 && direccionCambio && !selected;

    const verify = () => {
        if (!direccionEscrita) return;

        setVerifying(true);
        setErrors([]);
        setCandidates(null);
        setSelected(null);
        centreServices.verifyAddress(direccionEscrita)
            .then((data) => {
                const encontrados = data?.candidates || [];
                setCandidates(encontrados);
                // Un solo candidato y preciso: no hay nada que elegir, se deja elegido.
                if (encontrados.length === 1 && esPreciso(encontrados[0])) setSelected(encontrados[0]);
            })
            .catch(() => setErrors(['No se pudo consultar HERE. Intentá de nuevo.']))
            .finally(() => setVerifying(false));
    };

    const saveRecord = () => {
        if (!currentRecord) return;

        // Guardar sin haber verificado una dirección nueva es cómo terminaban las bodegas sin
        // coordenadas: se verifica primero y se pide elegir.
        if (necesitaVerificar) {
            setErrors(['Verificá la dirección y elegí una de las opciones antes de guardar.']);
            verify();
            return;
        }

        setSaving(true);
        setErrors([]);
        const payload = {
            centre: {
                ..._.pick(currentRecord, ['name', 'code']),
                address: selected?.address || direccionEscrita,
                ...(selected ? { location: selected } : {}),
            },
        };

        const after = (resp, isNew) => {
            (isNew ? onRecordAdd : onRecordChange)(resp);
            onClose();
            setSaving(false);
        };

        const onError = (err) => {
            setErrors(err?.response?.data?.errors || ['No fue posible guardar la bodega']);
            setSaving(false);
        };

        if (currentRecord.id) {
            centreServices.saveCentre(currentRecord.id, INCLUDE, payload)
                .then(resp => after(resp, false))
                .catch(onError);
        } else {
            centreServices.createCentre(INCLUDE, payload)
                .then(resp => after(resp, true))
                .catch(onError);
        }
    };

    const modalTitle = () => {
        if (loadingRecord) return 'Cargando';
        if (currentRecord?.id) return currentRecord.name;
        return 'Nueva bodega';
    };

    const renderCandidatos = () => {
        if (candidates === null) return null;

        if (candidates.length === 0) {
            return (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    HERE no encontró esa dirección. Probá con la calle, el número y la comuna.
                </Alert>
            );
        }

        return (
            <React.Fragment>
                <Typography variant="caption" color="text.secondary">
                    Elegí la dirección que corresponde:
                </Typography>
                <List dense disablePadding sx={{ maxHeight: 220, overflowY: 'auto' }}>
                    {candidates.map((c) => (
                        <ListItemButton
                            key={c.map_place_id || c.address}
                            selected={selected?.map_place_id === c.map_place_id}
                            onClick={() => setSelected(c)}
                        >
                            <ListItemText
                                primary={c.address}
                                secondary={`${NIVEL_LABEL[c.result_type] || c.result_type} · ${c.lat?.toFixed(5)}, ${c.lng?.toFixed(5)}`}
                            />
                            {!esPreciso(c) && <Chip size="small" color="warning" label="poco preciso" sx={{ ml: 1 }} />}
                        </ListItemButton>
                    ))}
                </List>
            </React.Fragment>
        );
    };

    // Qué va a quedar guardado: el candidato elegido, o lo que ya tenía la bodega.
    const renderUbicacion = () => {
        const ubicacion = selected || savedLocation;

        if (!ubicacion) {
            if (!currentRecord?.id) return null;

            return (
                <Alert severity="error" sx={{ mt: 1 }}>
                    Esta bodega no tiene coordenadas, así que el ruteador no puede usarla: los viajes que
                    salgan de acá no se van a poder calcular. Verificá la dirección y guardá.
                </Alert>
            );
        }

        const preciso = selected ? esPreciso(selected) : true;

        return (
            <Alert severity={preciso ? 'success' : 'warning'} sx={{ mt: 1 }}>
                <Typography variant="body2">
                    {selected ? 'Se va a guardar en ' : 'Ubicada en '}
                    {`${Number(ubicacion.lat).toFixed(5)}, ${Number(ubicacion.lng).toFixed(5)}`}
                    {selected ? ' con su geocerca.' : '.'}
                    {!preciso && ' HERE resolvió a un nivel poco preciso: la ruta va a arrancar del centro de la zona y no de la puerta de la bodega.'}
                </Typography>
            </Alert>
        );
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={isOpen} onClose={onClose}>
            <DialogTitle>
                <Typography variant="h2" color="primary">{modalTitle()}</Typography>
            </DialogTitle>
            <DialogContent>
                {errors.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errors.join('. ')}
                    </Alert>
                )}
                {loadingRecord ? (
                    <Div sx={{ textAlign: 'center' }}>
                        <CircularProgress />
                    </Div>
                ) : currentRecord && (
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                label="Nombre"
                                required
                                error={!currentRecord.name}
                                value={currentRecord.name || ''}
                                onChange={(e) => setCurrentRecord({ ...currentRecord, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Div sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Dirección"
                                    placeholder="Camino Real 4518, Lo Barnechea"
                                    value={currentRecord.address || ''}
                                    onChange={(e) => {
                                        setCurrentRecord({ ...currentRecord, address: e.target.value });
                                        setCandidates(null);
                                        setSelected(null);
                                    }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); verify(); } }}
                                />
                                <LoadingButton
                                    loading={verifying}
                                    variant="outlined"
                                    size="small"
                                    sx={{ mt: 0.25, flexShrink: 0 }}
                                    startIcon={<TravelExploreOutlinedIcon />}
                                    disabled={!direccionEscrita}
                                    onClick={verify}>
                                    Verificar
                                </LoadingButton>
                            </Div>
                            {renderCandidatos()}
                            {renderUbicacion()}
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Código"
                                helperText="Opcional: si lo dejás vacío se genera automáticamente."
                                value={currentRecord.code || ''}
                                onChange={(e) => setCurrentRecord({ ...currentRecord, code: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cerrar</Button>
                <LoadingButton
                    loading={saving}
                    variant="contained"
                    color="success"
                    size="small"
                    endIcon={<SaveOutlinedIcon />}
                    loadingPosition="end"
                    disabled={!currentRecord?.name}
                    onClick={saveRecord}>
                    {saving ? 'Guardando' : 'Guardar'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default CentreEditModal;
